import { useEffect, useRef, useState, useCallback } from 'react';
import { parseScannerAction } from 'components/Admin/ScannerControl/scannerActions';

export interface KeyboardScannerOptions {
    /** Minimum number of characters to be considered a valid barcode. Default: 6 */
    minLength?: number;
    /** Maximum average time per character in ms. Scanners type ≤35ms/char; humans type much slower. Default: 35 */
    maxAvgTime?: number;
    /** Idle ms after last keydown before the buffer is evaluated. Default: 80 */
    endTimeout?: number;
    /** Optional prefix character that must start the barcode. Default: null (disabled) */
    prefix?: string | null;
    /** Key name that terminates the scan sequence. Default: "Enter" */
    suffix?: string;
    /** Log timing diagnostics to the console. Default: false */
    debug?: boolean;
    /** Ignore keydown events when an INPUT, TEXTAREA, or contenteditable is focused. Default: true */
    ignoreInputs?: boolean;
}

interface UseBarcodeOptions {
    onScanMatch?: (key: string) => void;
    keyboardScanner?: KeyboardScannerOptions;
    enabled?: boolean;
}

export const useBarcodeScanning = ({ onScanMatch, keyboardScanner, enabled = true }: UseBarcodeOptions) => {
    const [highlightedKey, setHighlightedKey] = useState<string | null>(null);

    // Keyboard scanner state refs — avoids stale closures without adding to dep arrays
    const bufferRef = useRef('');
    const timestampsRef = useRef<number[]>([]);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Destructure keyboard scanner options so primitives can enter dep arrays safely
    const {
        minLength = 6,
        maxAvgTime = 35,
        endTimeout = 80,
        prefix = null,
        suffix = 'Enter',
        debug = false,
        ignoreInputs = true,
    } = keyboardScanner ?? {};

    // Shared handler called by both the paste path and the keydown path
    const handleScan = useCallback(
        (scannedValue: string) => {
            setHighlightedKey(scannedValue);
            onScanMatch?.(scannedValue);
        },
        [onScanMatch],
    );

    // ── Paste-based scanner ──────────────────────────────────────────────────
    useEffect(() => {
        if (!enabled) return;
        function handlePaste(event: ClipboardEvent) {
            if (!event || event.type !== 'paste') return;

            const active = document.activeElement;
            let pasted = '';
            if (event.clipboardData) {
                pasted = event.clipboardData.getData('text/plain')?.trim();
            } else if ((window as any).clipboardData) {
                pasted = (window as any).clipboardData.getData('Text')?.trim();
            }
            if (!pasted) return;

            const isJsonCandidate = pasted.trimStart().startsWith('{');
            const isScannerControlAction = isJsonCandidate && parseScannerAction(pasted) !== null;

            // Allow normal paste for elements marked with data-allow-paste,
            // UNLESS the pasted content is a valid scanner JSON control code.
            if (active && (active as HTMLElement).closest('[data-allow-paste]')) {
                if (!isScannerControlAction) {
                    return;
                }
                // Control code — fall through and intercept
            }

            // JSON that is not a recognized scanner action should be treated as normal paste.
            if (isJsonCandidate && !isScannerControlAction) {
                return;
            }

            // Blur input elements to prevent barcode from being typed into them
            if (
                active &&
                (active.tagName === 'INPUT' ||
                    active.tagName === 'TEXTAREA' ||
                    (active as HTMLElement).isContentEditable)
            ) {
                (active as HTMLElement).blur();
            }

            event.preventDefault();
            event.stopPropagation();

            handleScan(pasted);
        }

        // Capture phase ensures JSON control codes are intercepted before focused inputs handle paste.
        document.addEventListener('paste', handlePaste, true);
        return () => {
            document.removeEventListener('paste', handlePaste, true);
        };
    }, [handleScan, enabled]);

    // ── Keydown-based scanner (typing-speed scanners that end with Enter) ────
    useEffect(() => {
        if (!enabled) return;
        function finishScan() {
            const buffer = bufferRef.current;
            const timestamps = timestampsRef.current;

            if (buffer.length >= minLength && timestamps.length > 0) {
                const duration = timestamps[timestamps.length - 1] - timestamps[0];
                const avg = duration / buffer.length;

                if (debug) {
                    console.log(
                        '[BarcodeScanner] buffer:',
                        buffer,
                        '| chars:',
                        buffer.length,
                        '| duration:',
                        duration.toFixed(2),
                        'ms | avg/char:',
                        avg.toFixed(2),
                        'ms',
                    );
                }

                if (avg <= maxAvgTime) {
                    handleScan(buffer);
                }
            }

            bufferRef.current = '';
            timestampsRef.current = [];
        }

        function handleKeyDown(e: KeyboardEvent) {
            if (ignoreInputs) {
                const active = document.activeElement;
                if (
                    active &&
                    (active.tagName === 'INPUT' ||
                        active.tagName === 'TEXTAREA' ||
                        (active as HTMLElement).isContentEditable)
                ) {
                    return;
                }
            }

            // Ignore modifier/control keys (allow single printable chars and Enter)
            if (e.key.length > 1 && e.key !== 'Enter') return;

            const now = performance.now();

            if (bufferRef.current.length > 0) {
                timestampsRef.current.push(now);
            }

            if (prefix && bufferRef.current === '' && e.key !== prefix) {
                return;
            }

            if (e.key === suffix) {
                if (timerRef.current) clearTimeout(timerRef.current);
                finishScan();
                return;
            }

            bufferRef.current += e.key;

            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(finishScan, endTimeout);
        }

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            if (timerRef.current) clearTimeout(timerRef.current);
        };
    }, [handleScan, minLength, maxAvgTime, endTimeout, prefix, suffix, debug, ignoreInputs, enabled]);

    return {
        highlightedKey,
        clearHighlight: () => setHighlightedKey(null),
    };
};

export default useBarcodeScanning;
